// @flow

import './ProjectEditingContainer.css';

import React, {Component} from 'react';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import AdminList from '../../components/AdminList/AdminList';
import {touch, isValid, getFormValues, reset as resetForm} from 'redux-form';
import {
  fetchProject,
  fetchProducts,
  fetchRoomTypes,
  updateProject,
  uploadProjectFile,
  removeProjectFile,
  abortFileUpload,
  abortAllUploads,
  filterApartments,
  setActiveRoomType,
  uploadRoomTypeImage,
  removeRoomTypeImage,
  reset,
  clear,
  sendActivationLinks,
  sortItems,
  notifyLinkCopied,
} from '../../actions/projectView';
import {setDirty} from '../../actions/form';
import FileDropzone from '../../components/FileDropzone/FileDropzone';
import ProjectForm from '../../components/ProjectForm/ProjectForm';
import ProjectProductsForm from '../../components/ProjectProductsForm/ProjectProductsForm';
import ApartmentConfigList from '../../components/ApartmentConfigList/ApartmentConfigList';
import RoomTypeImages from '../../components/RoomTypeImages/RoomTypeImages';
import ServerError from '../../components/ServerError/ServerError';
import LoadingIndicator from '../../components/LoadingIndicator/LoadingIndicator';
import SaveMenu from '../../components/SaveMenu/SaveMenu';
import Navigation from '../../components/Navigation/Navigation';
import ActionConfirmation from '../ActionConfirmation/ActionConfirmation';
import SearchField from '../../components/SearchField/SearchField';
import validator from '../../helpers/validator';
import _ from 'lodash/fp';
import DisableContent from '../../components/DisableContent/DisableContent';

const apartmentColumns = [{
  property: 'objectId',
  header: {
    label: 'Lägenhetsnummer',
  },
}, {
  property: 'area',
  header: {
    label: 'Yta',
  },
}, {
  property: 'address',
  header: {
    label: 'Adress',
  },
}];

const projectValidation = {
  name: {rule: 'string|required', label: 'Namn'},
  propertyNumber: {rule: 'string|required', label: 'Fastighetsnummer'},
  propertyDescription: {rule: 'string|required', label: 'Fastighetsbeteckning'},
  description: {rule: 'string', label: 'Beskrivning'},
};

export type Props = {
  history: Object,
  project: ?any,
  apartments: ?any[],
  apartmentConfigurations: any[],
  demoApartmentConfig: Object,
  activeRoomType: ?any,
  projectFormValues: any,
  projectFormIsValid: boolean,
  projectProductsFormValues: any,
  products: any[],
  roomTypes: any[],
  projectFiles: Object,
  projectFileUploads: Object,
  projectFilesUploadIds: Object,
  roomTypeImages: any[],
  roomTypeImageUploads: Object,
  roomTypeImagesUploadIds: Object,
  isFetching: boolean,
  isFetchingRoomTypes: boolean,
  isFetchingProducts: boolean,
  params: any,
  error: ?any,
  prevPath: string,
  query: ?string,
  fetchData: (id: number) => any,
  updateProject: typeof updateProject,
  uploadProjectFile: typeof uploadProjectFile,
  removeProjectFile: typeof removeProjectFile,
  uploadRoomTypeImage: typeof uploadRoomTypeImage,
  removeRoomTypeImage: typeof removeRoomTypeImage,
  abortFileUpload: typeof abortFileUpload,
  abortAllUploads: typeof abortAllUploads,
  filterApartments: typeof filterApartments,
  setActiveRoomType: typeof setActiveRoomType,
  touch: typeof touch,
  clear: typeof clear,
  resetForm: typeof resetForm,
  setDirty: (dirty: boolean) => any,
  reset: typeof reset,
  isDirty: boolean,
  sendActivationLinks: typeof sendActivationLinks,
  projects: any[],
  filteredApartments: any[],
  sortItems: typeof sortItems,
  notifyLinkCopied: () => any,
};

class ProjectEditingContainer extends Component<Props> {
  props: Props;

  componentWillMount() {
    this.props.fetchData(this.props.params.id);
  }

  componentWillUnmount() {
    const {
      projectFilesUploadIds,
      roomTypeImagesUploadIds,
      abortAllUploads,
      clear,
    } = this.props;

    abortAllUploads(projectFilesUploadIds, roomTypeImagesUploadIds);
    clear();
  }

  setDirty() {
    const {
      setDirty,
      isDirty,
    } = this.props;

    if (!isDirty) {
      setDirty(true);
    }
  }

  reset() {
    const {
      setDirty,
      reset,
      resetForm,
    } = this.props;
    reset();
    resetForm('project');
    resetForm('projectProducts');
    setDirty(false);
  }

  save() {
    const {
      project,
      projectFormValues,
      projectFormIsValid,
      projectProductsFormValues,
      updateProject,
      projectFiles,
      roomTypeImages,
      touch,
    } = this.props;
    const projectFields = _.keys(projectValidation);
    touch('project', ...projectFields);
    if (!projectFormIsValid) {
      return;
    }

    const data = {
      ...projectFormValues,
      ...projectFiles,
      images: roomTypeImages,
    };

    if (projectProductsFormValues) {
      data.blacklistedProducts =
        projectProductsFormValues.blacklistedProducts;
    }

    if (!!project) {
      updateProject(project.id, data);
    }
  }

  render() {
    const {
      history,
      params: {id},
      prevPath,
      query,
      project,
      apartments,
      activeRoomType,
      error,
      isFetching,
      isFetchingRoomTypes,
      isFetchingProducts,
      updateProject,
      products,
      apartmentConfigurations,
      demoApartmentConfig,
      roomTypes,
      projectFiles,
      projectFileUploads,
      uploadProjectFile,
      abortFileUpload,
      removeProjectFile,
      filterApartments,
      setActiveRoomType,
      roomTypeImages,
      roomTypeImageUploads,
      uploadRoomTypeImage,
      removeRoomTypeImage,
      isDirty,
      sendActivationLinks,
      projects,
      clear,
      fetchData,
      sortItems,
      filteredApartments,
      notifyLinkCopied,
    } = this.props;

    return (
      <div className="relative">
        <div className="row align-justify align-middle content-header">
          <div className="column shrink">
            <div className="row no-row-margins align-middle">
              <Navigation
                current={project}
                list={projects}
                prevPath={prevPath}
                clear={clear}
                fetchData={fetchData} />
              <div className="content-title">
                {(project && project.name)}
              </div>
            </div>
          </div>
          <div className="column shrink horizontal-button-list">
            {project &&
              <DisableContent disabled={isDirty}>
                <div className="column shrink horizontal-button-list">
                  {!!apartments && !!apartments.length &&
                    <ActionConfirmation
                      id="sendActivationLinks"
                      className="button"
                      message="Är du säker på att du vill skicka aktiveringslänkar till alla användare som inte ännu fått en?"
                      buttonText="Skicka aktiveringslänkar"
                      action={() => sendActivationLinks(project.id)} />
                  }
                  <ActionConfirmation
                    id="archiveProject"
                    className="secondary button"
                    message={`Vill du verkligen ${project.archived ? 'av' : ''}arkivera det här projektet?`}
                    buttonText={project.archived ? 'Avarkivera' : 'Arkivera'}
                    action={() => updateProject(project.id, {
                      archived: !project.archived,
                    })} />
                </div>
              </DisableContent>
            }
          </div>
        </div>
        <LoadingIndicator isLoading={isFetching || isFetchingRoomTypes || isFetchingProducts} />
        <ServerError error={error} />
        <div className="content-section box">
          <div className="row">
            <div className="column small-6">
              <ProjectForm
                initialValues={project}
                onUserChange={() => this.setDirty()}
                validate={values => validator(projectValidation, values)} />
            </div>
            <div className="column small-6">
              <FileDropzone
                label="Cover image"
                accept="image/png, image/jpg, image/jpeg, image/bmp"
                preview={true}
                file={projectFiles.coverImage}
                upload={projectFileUploads.coverImage}
                onUpload={f => uploadProjectFile('coverImage', f)}
                onUploadAbort={u => abortFileUpload(u.tempId)}
                onRemove={() => removeProjectFile('coverImage')}
                onChange={() => this.setDirty()} />
              <FileDropzone
                label="Broschyr"
                file={projectFiles.brochure}
                upload={projectFileUploads.brochure}
                onUpload={f => uploadProjectFile('brochure', f)}
                onUploadAbort={u => abortFileUpload(u.tempId)}
                onRemove={() => removeProjectFile('brochure')}
                onChange={() => this.setDirty()} />
              <hr />
              <button
                type="button"
                onClick={() =>  history.push(`${prevPath}/${id}/apartmentconfigurations/${demoApartmentConfig.id}`)}
                className="button w-100">
                Öppna demokonfiguration
              </button>
              { project &&
              <div className="demoContainer">
                <input
                  id="demo-url"
                  type="text"
                  readOnly
                  value={`${location.origin}/demo/${project.demoPath}`}>
                </input>

                <button
                  type="button"
                  className="demo-url-text button"
                  onClick={
                    () => {
                      /* Get the text field */
                      const copyText = document.getElementById('demo-url');

                      /* Select the text field */
                      copyText.select();
                      copyText.setSelectionRange(0, 99999); /*For mobile devices*/

                      /* Copy the text inside the text field */
                      document.execCommand('copy');

                      /* Unfocus the text box when we are done */
                      copyText.blur();

                      /* Alert the copied text */
                      // alert(`Copied the text: ${copyText.value}`);
                      notifyLinkCopied();
                    }
                  }>
                Kopiera
                </button>
              </div>
              }

            </div>
          </div>
        </div>
        <div className="content-section box">
          <div className="row">
            <div className="column small-6">
              <ApartmentConfigList
                list={apartmentConfigurations}
                onSelectItem={c => history.push(`${prevPath}/${id}/apartmentconfigurations/${c.id}`)} />
              <hr />
              <button
                onClick={() => history.push(`${prevPath}/${id}/apartmentconfigurations/new`)}
                type="button"
                className="button small expanded">
                Skapa ny
              </button>
            </div>
            <div className="column small-6">
              <RoomTypeImages
                activeRoomType={activeRoomType}
                roomTypes={roomTypes}
                images={roomTypeImages}
                uploads={roomTypeImageUploads}
                onRoomTypeSelect={r => setActiveRoomType(r)}
                onUploadAbort={u => abortFileUpload(u.tempId)}
                onUpload={f => {
                  this.setDirty();
                  if (!!activeRoomType) {
                    uploadRoomTypeImage(activeRoomType.id, f);
                  }
                }}
                onRemove={i => {
                  this.setDirty();
                  removeRoomTypeImage(i.id);
                }} />
            </div>
          </div>
        </div>
        <div className="content-section box">
          <label>Lägenheter</label>
          <div className="row align-middle">
            {apartments && !!apartments.length &&
              <div className="column">
                <SearchField
                  value={query || ''}
                  onClear={e => filterApartments('')}
                  onChange={e => filterApartments(e.target.value)} />
              </div>
            }
            <div className="column shrink">
              <button
                type="button"
                className="primary button"
                onClick={() => history.push(`${prevPath}/${id}/apartments/new`)}>
                  Skapa ny
              </button>
            </div>
          </div>
          {apartments && !!apartments.length &&
            <AdminList
              onRowSelected={row => history.push(`${prevPath}/${id}/apartments/${row.id}`)}
              onSort={sortItems}
              columns={apartmentColumns}
              rows={filteredApartments} />
          }
        </div>
        <div className="content-section box">
          <div className="row">
            <div className="column small-12">
              <ProjectProductsForm
                products={products}
                onUserChange={() => this.setDirty()}
                initialValues={project} />
            </div>
          </div>
        </div>
        {isDirty &&
          <SaveMenu
            isLoading={isFetching}
            onDiscard={() => this.reset()}
            onSave={() => this.save()}/>
        }
      </div>
    );
  }
}

const getProjectFileUploads = (uploadIds, files) => {
  return _.mapValues(id => files.byId[id], uploadIds) || {};
};

const getRoomTypeImagesUploads = (uploadIds, files) => {
  return _.mapValues(_.map(u => files.byId[u]), uploadIds) || {};
};

const mapStateToProps = ({
  projectView: {
    project: {
      active: project,
      error,
      isFetching,
      apartments,
      apartmentConfigurations,
      files: projectFiles,
      roomTypeImages,
    },
    products: {
      list: products,
      isFetching: isFetchingProducts,
    },
    filteredRoomTypes: roomTypes,
    roomTypes: {
      isFetching: isFetchingRoomTypes,
    },
    roomType: {
      active: activeRoomType,
    },
    uploadIds: {
      projectFiles: projectFilesUploadIds,
      roomTypeImages: roomTypeImagesUploadIds,
    },
    apartmentsFilter: {
      query,
    },
    isDirty,
    filteredApartments,
  },
  projectList: {
    filteredList: projects,
  },
  form,
  files,
}) => ({
  project,
  error,
  isFetching,
  isFetchingRoomTypes,
  isFetchingProducts,
  apartments,
  apartmentConfigurations: _.filter({'demo': false}, apartmentConfigurations),
  demoApartmentConfig: _.find('demo', apartmentConfigurations),
  products,
  roomTypes,
  projectFiles,
  projectFilesUploadIds,
  activeRoomType,
  roomTypeImages,
  roomTypeImagesUploadIds,
  query,
  roomTypeImageUploads: getRoomTypeImagesUploads(roomTypeImagesUploadIds, files),
  projectFileUploads: getProjectFileUploads(projectFilesUploadIds, files),
  projectFormIsValid: isValid('project')({form}),
  projectFormValues: getFormValues('project')({form}),
  projectProductsFormValues: getFormValues('projectProducts')({form}),
  isDirty,
  projects,
  filteredApartments: _.filter({'demo': false}, filteredApartments),
});

const mapDispatchToProps = dispatch => ({
  fetchData: id => {
    dispatch(fetchProducts());
    dispatch(fetchRoomTypes());
    if (id) {
      dispatch(fetchProject(id));
    }
  },
  updateProject: (id, data) =>
    dispatch(updateProject(id, data)),
  touch: (form, ...fields) =>
    dispatch(touch(form, ...fields)),
  removeProjectFile: name =>
    dispatch(removeProjectFile(name)),
  abortFileUpload: tempId =>
    dispatch(abortFileUpload(tempId)),
  abortAllUploads: (uploads, roomTypeImageUploads) =>
    dispatch(abortAllUploads(uploads, roomTypeImageUploads)),
  uploadProjectFile: (name, [file, ...rest]) => {
    if (file) {
      dispatch(uploadProjectFile(name, file));
    }
  },
  uploadRoomTypeImage: (roomTypeId, [file, ...rest]) => {
    if (file) {
      dispatch(uploadRoomTypeImage(roomTypeId, file));
    }
  },
  removeRoomTypeImage: fileId =>
    dispatch(removeRoomTypeImage(fileId)),
  filterApartments: query =>
    dispatch(filterApartments(query)),
  setActiveRoomType: roomType =>
    dispatch(setActiveRoomType(roomType)),
  clear: () =>
    dispatch(clear()),
  setDirty: dirty =>
    dispatch(setDirty(dirty, 'project')),
  resetForm: form =>
    dispatch(resetForm(form)),
  reset: () =>
    dispatch(reset()),
  sendActivationLinks: (projectId: number) =>
    dispatch(sendActivationLinks(projectId)),
  sortItems: sorter =>
    dispatch(sortItems(sorter)),
  notifyLinkCopied: () =>
    dispatch(notifyLinkCopied()),
});

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(ProjectEditingContainer));



// WEBPACK FOOTER //
// ./src/containers/ProjectEditingContainer/ProjectEditingContainer.js